/**
 * @file BlendBriefModal.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-09-17
 * @description "18% protein, 5% fibre, use Rolled Wheat and Propass" → blends.
 *
 *              Two panels, and the first one matters more than the second. The
 *              left shows what the sentence was understood to mean, in the same
 *              terms the rest of the calculator uses — a floor, a ceiling, a
 *              named material — and it says so before anything is applied.
 *              Something read wrongly is then obvious at a glance rather than
 *              discovered later in a blend that quietly missed the point.
 *
 *              The right lists the answers. They are not variations on a guess:
 *              the LP is exact, so there is exactly one cheapest blend per
 *              reading of the brief, and what the options differ on is the
 *              reading — hold the figures literally, allow the half point of
 *              give a feed label allows, or spend only on what was named. The
 *              price gap between the first two is the cost of being literal,
 *              and it is usually the most useful number on the screen.
 *
 *              Rules about the blend as a whole — a cost ceiling, how many
 *              ingredients, how many different blends — are read into the brief
 *              and kept by every answer (1.4.0). When nothing can keep them, the
 *              panel says which rule could not be met instead of a generic
 *              "nothing found". Two example requests sit under the box, because
 *              the fastest way to learn what it understands is to see it work.
 *
 *              NOTHING IS SENT UNTIL "BUILD IT" (1.5.0)
 *
 *              Reading a request costs a Claude call; building from a reading
 *              costs nothing, because the solver runs here. So an example chip
 *              only fills the box — it used to run straight away, one call per
 *              click, for a request the user then usually edited and ran again.
 *              And a request already read in this modal is not read twice:
 *              pressing Build it again, after changing the starting blend say,
 *              rebuilds from the reading it already has. The server keeps its own
 *              cache of readings as well (blendBriefService).
 *
 *              There is no default minimum inclusion either: an ingredient is
 *              anything the blend contains, unless the request set a minimum.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Modal from './Modal';
import { API_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';
import { parseBrief, parseRules, buildOptionsFromBrief, findReferences } from '../utils/blendBrief';
import { describeRules } from '../utils/blendSolver';
import { countIngredients, isIncluded, formatInclusion } from '../utils/blendInclusion';
import { formatCurrency } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';
import './BlendBriefModal.css';

const num = (v, d = 2) =>
  (v === null || v === undefined || !Number.isFinite(Number(v))) ? '—' : Number(v).toFixed(d);

/**
 * Requests worth clicking. Kept exactly as Cam typed them, spelling included —
 * "varations" is the point of the first one: a request typed at speed must
 * still be read properly.
 */
export const EXAMPLE_BRIEFS = [
  'Under £250 per tonne, 16% protein, no urea, minimum 2% molasses, minimum 4 ingredients, three varations, < 50% wheatfeed, include wheat gluten',
  'minimum 4 ingredients maximum 7'
];

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * The same request, however it was spaced or capitalised. Mirrors the server's
 * cache key, so "Build it" twice on the same words is one reading.
 */
const requestKey = (text) => String(text || '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();

function BriefBody({
  text, setText, brief, options, working, chosen, setChosen,
  materials, components, onRun,
  refs, base, onPickBase, baseLoading, reading, aiNotice, aiReady,
  outcome
}) {
  useSlideOutButton([working, options.length]);

  const inputRef = useRef(null);

  // An example fills the box and hands it back to the user, caret at the end —
  // ready to edit or to run. It does not run: running is a Claude call, and it
  // is the Build it button's job alone.
  const fillExample = (example) => {
    setText(example);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(example.length, example.length);
      el.scrollTop = el.scrollHeight;
    });
  };

  const nameOf = useMemo(() => {
    const map = new Map(materials.map((m) => [m.key, m.name]));
    return (key) => map.get(key) || key;
  }, [materials]);

  const labelOf = useMemo(() => {
    const map = new Map((components || []).map((c) => [c.key, c.label]));
    return (key) => map.get(key) || key;
  }, [components]);

  const targetEntries = Object.entries(brief?.targets || {});

  // The whole-blend rules as chips, in the same words a refusal would use.
  const ruleChips = [
    describeRules({ minIngredients: brief?.minIngredients, maxIngredients: brief?.maxIngredients }),
    describeRules({ minInclusion: brief?.minInclusion }),
    describeRules({ costMax: brief?.costMax })
  ].filter(Boolean).map(capitalise);
  const countsIngredients = !!(brief?.minIngredients || brief?.maxIngredients);

  const readNothing = targetEntries.length === 0
    && !brief?.require?.length
    && !brief?.exclude?.length
    && !Object.keys(brief?.bounds || {}).length
    && !ruleChips.length
    && !(brief?.optionCount > 1);

  return (
    <div className="bbm-body">
      <div className="bbm-input">
        <textarea
          ref={inputRef}
          rows={2}
          value={text}
          autoFocus
          placeholder="18% protein, 5% fibre, 20% starch, use Rolled Wheat and Propass"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onRun(); }
          }}
          aria-label="Describe the blend you want"
        />
        <button className="btn-slideout" onClick={onRun} disabled={working || !text.trim()}>
          <div className="icon-wrapper">
            <span className={`material-symbols-rounded${working ? ' spinning' : ''}`}>
              {working ? 'progress_activity' : 'auto_awesome'}
            </span>
          </div>
          <span className="btn-label">{working ? 'Working…' : 'Build it'}</span>
        </button>
      </div>

      {/* Chips rather than slide-outs: the request IS the label, and a
          slide-out would hide it until hovered. The same reasoning as the
          suggestion cards below, which are buttons carrying their own text. */}
      <div className="bbm-examples">
        <span className="bbm-examples-label">Examples</span>
        {EXAMPLE_BRIEFS.map((example) => (
          <button
            key={example}
            type="button"
            className="bbm-example"
            disabled={working}
            title="Use this example — it fills the box. Edit it, then press Build it."
            aria-label={`Use this example: ${example}. Fills the box; edit it, then press Build it.`}
            onClick={() => fillExample(example)}
          >
            {example}
          </button>
        ))}
      </div>

      {/* Said plainly. This reads a sentence and guesses; it is right often
          enough to be useful and not often enough to be trusted blind. */}
      <p className="bbm-experimental">
        <span className="material-symbols-rounded">science</span>
        <span>
          Experimental. It reads plain English and can misread it — check what it
          understood on the left before using an answer. Ideas for what it should
          handle next: <a href="mailto:cam@excel-agri.com?subject=Blend%20builder">cam@excel-agri.com</a>
        </span>
      </p>

      <div className="bbm-split">
        {/* What it understood, before it acts on it. */}
        <section className="bbm-read">
          <h4>What I read</h4>

          {/* The model's own one-line summary, when it read the request. This is
              the check that matters: the constraints below are exact, but a
              sentence is what makes a misreading obvious at a glance. */}
          {reading && (
            <p className="bbm-reading">
              <span className="material-symbols-rounded">auto_awesome</span>
              {reading}
            </p>
          )}

          {/* The model could not be asked, so the plain parse answered. Said,
              because the two read very differently and nobody should assume
              the sentence had the model's reading when it did not. */}
          {aiNotice && (
            <p className="bbm-reading bbm-ai-paused">
              <span className="material-symbols-rounded">pause_circle</span>
              {aiNotice} Read with the basic parser instead.
            </p>
          )}

          {/* Held analysis, when the request asked to keep the base's spec.
              Named explicitly because the figures were never typed — they came
              off the blend, and it should be plain that they did. */}
          {brief?.matchBase && (
            <p className="bbm-reading bbm-reading-quiet">
              <span className="material-symbols-rounded">lock</span>
              Holding the starting blend's own analysis, read off its recipe.
            </p>
          )}

          {/* The two instructions that used to land in "could not find". They
              are about the starting blend rather than about a material, so
              naming them here is what shows they were acted on. */}
          {brief?.boundsFromBase === 'max' && (
            <p className="bbm-reading bbm-reading-quiet">
              <span className="material-symbols-rounded">vertical_align_top</span>
              Using the starting blend's own inclusions as ceilings — no more of
              anything it already has, though other materials may come in.
            </p>
          )}
          {brief?.boundsFromBase === 'min' && (
            <p className="bbm-reading bbm-reading-quiet">
              <span className="material-symbols-rounded">vertical_align_bottom</span>
              Using the starting blend's own inclusions as floors — at least what
              it already has of each.
            </p>
          )}
          {brief?.optionCount > 1 && (
            <p className="bbm-reading bbm-reading-quiet">
              <span className="material-symbols-rounded">format_list_numbered</span>
              {brief.optionCount} different blends, each leaving out at least one
              main ingredient of the ones before it — not the same blend relabelled.
            </p>
          )}

          {/* What counts as an ingredient. Said, because it decides what
              "minimum 4 ingredients" is allowed to mean — and there is no
              minimum unless the request gave one. */}
          {countsIngredients && (
            <p className="bbm-reading bbm-reading-quiet">
              <span className="material-symbols-rounded">tag</span>
              {brief?.minInclusion
                ? `Counting an ingredient only at ${formatInclusion(brief.minInclusion)}% of the blend or more, as asked.`
                : 'Counting every material in the blend, however little of it. For a minimum, say so — “each ingredient at least 1%”.'}
            </p>
          )}

          {/* The starting point, when one was named. Never assumed silently:
              the match is fuzzy and a wrong base produces a plausible blend for
              the wrong customer, which is worse than no suggestion at all. */}
          {refs?.baseCandidates?.length > 0 && (
            <div className="bbm-base">
              <span className="bbm-base-label">Starting from</span>
              <select
                value={base ? `${base.kind}:${base.id}` : ''}
                onChange={(e) => onPickBase(e.target.value)}
              >
                <option value="">Nothing — build from scratch</option>
                {refs.baseCandidates.map((c) => (
                  <option key={`${c.kind}:${c.id}`} value={`${c.kind}:${c.id}`}>
                    {c.kind === 'formulation' ? 'Formulation' : 'Blend'} · {c.name}
                    {c.sub ? ` · ${c.sub}` : ''}
                  </option>
                ))}
              </select>
              {baseLoading && <span className="bbm-base-loading">loading…</span>}
            </div>
          )}

          {refs?.customer && !refs?.baseCandidates?.length && (
            <p className="bbm-unmatched">
              <span className="material-symbols-rounded">help</span>
              {refs.customer.name} has nothing on file that matches this — building
              from scratch.
            </p>
          )}

          {readNothing && (
            <p className="bbm-nothing">
              Nothing yet. Give it figures — “16% protein, max 6% fibre” — and
              the materials you want used, if any.
            </p>
          )}

          {targetEntries.length > 0 && (
            <ul className="bbm-chips">
              {targetEntries.map(([key, t]) => (
                <li key={key} className="bbm-chip">
                  <span className="bbm-chip-label">{labelOf(key)}</span>
                  <span className="bbm-chip-value">
                    {t.min != null && t.max != null ? `${num(t.min)}–${num(t.max)}`
                      : t.min != null ? `at least ${num(t.min)}`
                      : `at most ${num(t.max)}`}
                  </span>
                  {/* A bare figure was read as a floor. Saying so is the
                      difference between a tool you can trust and one that
                      guessed on your behalf. */}
                  {t._bare && <span className="bbm-chip-note">read as a minimum</span>}
                </li>
              ))}
            </ul>
          )}

          {ruleChips.length > 0 && (
            <div className="bbm-group">
              <span className="bbm-group-label">Rules</span>
              <span className="bbm-mats">
                {ruleChips.map((chip) => <span key={chip} className="bbm-mat">{chip}</span>)}
              </span>
            </div>
          )}

          {brief?.require?.length > 0 && (
            <div className="bbm-group">
              <span className="bbm-group-label">Use</span>
              <span className="bbm-mats">
                {brief.require.map((k) => <span key={k} className="bbm-mat">{nameOf(k)}</span>)}
              </span>
            </div>
          )}

          {brief?.exclude?.length > 0 && (
            <div className="bbm-group">
              <span className="bbm-group-label">Avoid</span>
              <span className="bbm-mats">
                {brief.exclude.map((k) => <span key={k} className="bbm-mat bbm-mat-out">{nameOf(k)}</span>)}
              </span>
            </div>
          )}

          {Object.keys(brief?.bounds || {}).length > 0 && (
            <div className="bbm-group">
              <span className="bbm-group-label">Limits</span>
              <span className="bbm-mats">
                {Object.entries(brief.bounds).map(([k, b]) => (
                  <span key={k} className="bbm-mat">
                    {nameOf(k)} {b.min != null ? `≥${b.min}` : ''}{b.max != null ? ` ≤${b.max}` : ''}%
                  </span>
                ))}
              </span>
            </div>
          )}

          {/* Never dropped silently — a material it could not find is the most
              likely reason an answer looks wrong. */}
          {brief?.unmatched?.length > 0 && (
            <p className="bbm-unmatched">
              <span className="material-symbols-rounded">help</span>
              Could not find {brief.unmatched.map((u) => `“${u}”`).join(', ')} in
              the material list, so it was left out.
            </p>
          )}
        </section>

        <section className="bbm-options">
          <h4>Suggestions</h4>

          {working && <LoadingSpinner />}

          {!working && options.length === 0 && (
            <p className="bbm-nothing">
              {/* The reason, when the builder has one: which rule could not be
                  kept, or what the ceiling is costing. */}
              {outcome?.message
                || (brief && Object.keys(brief.targets).length
                  ? 'No blend of the available materials meets that. Loosen a figure, or allow more materials.'
                  : 'Describe what you want and press Build it.')}
            </p>
          )}

          {!working && options.length > 0 && outcome?.shortfall && (
            <p className="bbm-unmatched">
              <span className="material-symbols-rounded">info</span>
              {outcome.shortfall}
            </p>
          )}

          {!working && options.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`bbm-option${chosen === option.key ? ' chosen' : ''}`}
              onClick={() => setChosen(option.key)}
            >
              <span className="bbm-option-head">
                <span className="bbm-option-title">{option.title}</span>
                <span className="bbm-option-cost">{formatCurrency(option.cost)}/T</span>
              </span>
              <span className="bbm-option-note">{option.note}</span>
              <span className="bbm-option-facts">
                {(() => {
                  const n = countIngredients(option.values);
                  return `${n} ingredient${n === 1 ? '' : 's'}`;
                })()}
                {/* The search stopped at its limit: the blend keeps every rule,
                    but a cheaper one might exist — by no more than this. */}
                {option.proved === false && (
                  <span className="bbm-option-unproved">
                    {' · '}best found in the time allowed
                    {option.searchGap > 0.005 ? ` — at most ${formatCurrency(option.searchGap)}/T above the cheapest` : ''}
                  </span>
                )}
              </span>
              {/* How far it moved, for the options built by adjusting an
                  existing blend. It is the figure that decides between them —
                  cost is secondary when the request was "keep it the same". */}
              {option.fromBase && option.movedTotal !== undefined && (
                <span className="bbm-option-moved">
                  {option.movedTotal === 0
                    ? 'Unchanged — it already meets this'
                    : `${option.movedTotal.toFixed(2)} percentage points moved`}
                </span>
              )}

              <span className="bbm-option-mix">
                {Object.entries(option.values)
                  .filter(([, v]) => isIncluded(v))
                  .sort((a, b) => b[1] - a[1])
                  .map(([key, v]) => (
                    <span key={key} className="bbm-part">
                      <strong>{num(v)}%</strong> {nameOf(key)}
                    </span>
                  ))}
              </span>

              {/* A ceiling the user did not ask for is still a decision made
                  on their behalf, so it is stated. Without these the cheapest
                  way to hit a protein figure and dilute to 100% is a third of
                  a tonne of limestone, which is arithmetic rather than feed. */}
              {option.capped?.length > 0 && (
                <span className="bbm-option-capped">
                  Held to normal inclusion limits: {option.capped.map((k) => nameOf(k)).join(', ')}
                </span>
              )}

              {Object.keys(brief?.targets || {}).length > 0 && (
                <span className="bbm-option-spec">
                  {Object.keys(brief.targets).map((key) => (
                    <span key={key} className="bbm-spec-hit">
                      {labelOf(key)} <strong>{num(option.nutrients[key])}</strong>
                    </span>
                  ))}
                </span>
              )}
            </button>
          ))}
        </section>
      </div>
    </div>
  );
}

function BlendBriefModal({
  initialText = '', materials, book, components, columnKey,
  // Everything the brief can refer to by name, so "take Chapple's blend and…"
  // resolves without a second trip through a picker.
  catalogue = null,
  loadBaseValues = null,
  onApply, onClose
}) {
  const [text, setText] = useState(initialText);
  const [brief, setBrief] = useState(null);
  const [options, setOptions] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [working, setWorking] = useState(false);
  const [refs, setRefs] = useState(null);
  const [base, setBase] = useState(null);
  const [baseValues, setBaseValues] = useState(null);
  const [baseLoading, setBaseLoading] = useState(false);
  // Whether the server can read a request in plain English. Null while unknown,
  // so the hint does not flicker between the two wordings on open.
  const [aiReady, setAiReady] = useState(null);
  const [reading, setReading] = useState('');
  const [aiNotice, setAiNotice] = useState('');
  // Why the last build came back empty or short, in the builder's own words.
  const [outcome, setOutcome] = useState(null);
  // Readings the model has already given in this modal, by requestKey. Only
  // successful ones: a fallback is not remembered, so the next press asks again.
  const aiReadings = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/blends/brief/available`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { available: false }))
      .then((d) => { if (!cancelled) setAiReady(!!d.available); })
      .catch(() => { if (!cancelled) setAiReady(false); });
    return () => { cancelled = true; };
  }, []);

  /*
   * Read the request.
   *
   * The model translates into the same brief the regex parse produces, and the
   * LP still does every bit of the blending — so what comes back is a set of
   * constraints, never a recipe. If it is not configured, is slow, or fails,
   * the regex parse answers instead: the feature degrades to what it was rather
   * than presenting an error for something that still works.
   */
  const readRequest = useCallback(async (raw) => {
    const local = parseBrief(raw, materials);
    if (aiReady === false) return local;
    try {
      const key = requestKey(raw);
      let ai = aiReadings.current.get(key) || null;
      if (!ai) {
        const res = await fetch(`${API_URL}/api/blends/brief/interpret`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: raw })
        });
        if (!res.ok) {
          // A paused API account is worth saying; any other failure falls back quietly.
          const body = await res.json().catch(() => ({}));
          return body?.code === 'AI_ACCOUNT_PAUSED' ? { ...local, aiNotice: body.error } : local;
        }
        const data = await res.json();
        if (!data?.brief) return local;
        ai = data.brief;
        aiReadings.current.set(key, ai);
      }
      // Keep the regex parse's material mentions as a floor: it reads "use
      // Propass" reliably, and losing one to a model that skipped it would be a
      // silent downgrade.
      const excluded = ai.exclude || [];
      // The same floor for the rules it reads just as literally — "minimum 4
      // ingredients", "under £250 per tonne", "< 50% wheatfeed". The model's
      // reading wins wherever it gave one.
      const either = (field) => ai[field] ?? local[field] ?? null;
      return {
        ...ai,
        require: [...new Set([...(ai.require || []), ...local.require])]
          .filter((k) => !excluded.includes(k)),
        bounds: {
          ...Object.fromEntries(Object.entries(local.bounds || {}).filter(([k]) => !excluded.includes(k))),
          ...(ai.bounds || {})
        },
        costMax: either('costMax'),
        minIngredients: either('minIngredients'),
        maxIngredients: either('maxIngredients'),
        minInclusion: either('minInclusion'),
        optionCount: either('optionCount'),
        // A phrase the model could not place but the plain reader could is not
        // unmatched any more — reporting it would say a rule was dropped when
        // it was kept.
        unmatched: (ai.unmatched || []).filter((u) => {
          const r = parseRules(u);
          return !(r.costMax || r.minIngredients || r.maxIngredients || r.minInclusion || r.optionCount);
        })
      };
    } catch (_) {
      return local;
    }
  }, [materials, aiReady]);

  // Parse as they type — reading back the understanding is free and immediate,
  // and it is what stops a misread brief becoming a wrong blend. Solving is
  // not: that waits to be asked.
  useEffect(() => {
    setBrief(text.trim() ? parseBrief(text, materials) : null);
    setRefs(text.trim() && catalogue ? findReferences(text, catalogue) : null);
  }, [text, materials, catalogue]);

  // The best match becomes the default starting point, but only while the user
  // has not chosen one — otherwise typing another word would silently move the
  // base out from under a decision they had already made.
  const touchedBase = useRef(false);
  useEffect(() => {
    if (touchedBase.current) return;
    setBase(refs?.base || null);
  }, [refs]);

  // Fetch the chosen base's recipe. Without it there is nothing to stay close
  // to, and the builder falls back to building from scratch.
  useEffect(() => {
    let cancelled = false;
    if (!base || !loadBaseValues) { setBaseValues(null); return undefined; }
    setBaseLoading(true);
    loadBaseValues(base)
      .then((values) => { if (!cancelled) setBaseValues(values || null); })
      .catch(() => { if (!cancelled) setBaseValues(null); })
      .finally(() => { if (!cancelled) setBaseLoading(false); });
    return () => { cancelled = true; };
  }, [base, loadBaseValues]);

  const pickBase = (value) => {
    touchedBase.current = true;
    if (!value) { setBase(null); return; }
    const [kind, id] = value.split(':');
    setBase((refs?.baseCandidates || []).find(
      (c) => c.kind === kind && String(c.id) === id) || null);
  };

  // Only ever from Build it (or ⌘/Ctrl+Enter in the box): this is the one place
  // a request is sent to be read.
  const run = async () => {
    const request = text;
    if (!request.trim()) return;
    setWorking(true);
    setChosen(null);
    setOutcome(null);
    try {
      const parsed = await readRequest(request);
      setBrief(parsed);
      setReading(parsed.reading || '');
      setAiNotice(parsed.aiNotice || '');

      /*
       * The model names a base in WORDS; findReferences resolves it against the
       * catalogue, which is already limited to what this user may see. So a
       * base can only ever be something they could have picked themselves.
       *
       * Only when they have not chosen one — a name in the sentence must not
       * move a base out from under a decision already made.
       */
      let values = baseValues;
      let name = base?.name || '';
      if (parsed.baseName && !touchedBase.current && catalogue && loadBaseValues) {
        const found = findReferences(parsed.baseName, catalogue);
        if (found?.base) {
          setBase(found.base);
          name = found.base.name;
          values = await loadBaseValues(found.base).catch(() => null);
          setBaseValues(values || null);
        }
      }

      // One frame for "Working…" to paint. The build runs on this thread and an
      // ingredient search can take a noticeable moment (it is capped at a few
      // seconds); without the yield a regex-only read goes straight into it and
      // the button looks as if the click did nothing.
      await new Promise((resolve) => setTimeout(resolve, 0));
      const built = buildOptionsFromBrief({
        brief: parsed, book, materials, columnKey,
        baseValues: values, baseName: name
      });
      setOptions(built.options);
      setOutcome({ message: built.message, shortfall: built.shortfall });
      if (built.options.length) setChosen(built.options[0].key);
    } finally {
      setWorking(false);
    }
  };

  const picked = options.find((o) => o.key === chosen);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Build me a blend"
      className="blend-brief"
      size="xl"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" disabled={!picked}
                    onClick={() => { onApply(picked); onClose(); }}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
              <span className="btn-label">Use this blend</span>
            </button>
          </div>
        </div>
      }
    >
      <BriefBody
        text={text}
        setText={setText}
        brief={brief}
        options={options}
        working={working}
        chosen={chosen}
        setChosen={setChosen}
        materials={materials}
        components={components}
        onRun={run}
        refs={refs}
        base={base}
        onPickBase={pickBase}
        baseLoading={baseLoading}
        reading={reading}
        aiNotice={aiNotice}
        aiReady={aiReady}
        outcome={outcome}
      />
    </Modal>
  );
}

export default BlendBriefModal;
